import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Modal, Button, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiEdit, FiSearch, FiUserCheck, FiChevronDown } from 'react-icons/fi';

export default function Users() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [saveLoading, setSaveLoading] = useState(false);
  
  useEffect(() => {
    async function fetchData() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/clients`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const data = await response.json();
        
        // Validate that data is an array before proceeding
        if (!Array.isArray(data)) {
          console.error('Expected array of clients, got:', typeof data);
          setLoading(false);
          return;
        }
  
        // Map the backend data to match the DataGrid's required row format
        const formattedRows = data.map((client, index) => {
          // Add null checks for client object
          if (!client) return null;
          
          return {
            id: index + 1,
            email: client.email || '',
            role: client.role || 'user', // Default to 'user' if undefined
            firstName: client.firstName || '',
            lastName: client.lastName || '',
            address: client.address || '',
            city: client.city || '',
            postalCode: client.postalCode || '',
            country: client.country || '',
            fullAddress: `${client.address || ''}, ${client.postalCode || ''} ${client.city || ''}, ${client.country || ''}`,
            phone: client.phone || 'Non specificato',
            createdAt: client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A',
            rawCreatedAt: client.createdAt || new Date().toISOString(),
            originalData: client // Keep original data for reference
          };
        }).filter(Boolean); // Filter out any null entries
  
        setRows(formattedRows);
        setFilteredRows(formattedRows);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    }
  
    fetchData();
  }, []);

  // Filter users based on search term and role
  useEffect(() => {
    let result = [...rows];
    
    // Filter by search term
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.firstName.toLowerCase().includes(lowercasedTerm) ||
        user.lastName.toLowerCase().includes(lowercasedTerm) ||
        user.email.toLowerCase().includes(lowercasedTerm) ||
        user.phone.toLowerCase().includes(lowercasedTerm) ||
        user.fullAddress.toLowerCase().includes(lowercasedTerm)
      );
    }
    
    // Filter by role
    if (filterRole !== 'all') {
      result = result.filter(user => user.role === filterRole);
    }
    
    setFilteredRows(result);
  }, [searchTerm, filterRole, rows]);

  const handleEditClick = (userId) => {
    const userToEdit = rows.find(user => user.id === userId);
    setSelectedUser(userToEdit);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  const handleSaveUser = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get('adminKey');
      
      // Prepare data for API
      const updatedUserData = {
        email: selectedUser.email,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        phone: selectedUser.phone,
        address: selectedUser.address,
        city: selectedUser.city,
        postalCode: selectedUser.postalCode,
        country: selectedUser.country,
        role: selectedUser.role
      };

      // Call API to update user
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/updateUser`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Admin-Token': adminToken
        },
        body: JSON.stringify({
          email: selectedUser.email,  // Use email as identifier
          userData: updatedUserData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      // Update local state with new values
      const updatedRows = rows.map(user => {
        if (user.id === selectedUser.id) {
          return {
            ...user,
            ...updatedUserData,
            fullAddress: `${updatedUserData.address || ''}, ${updatedUserData.postalCode || ''} ${updatedUserData.city || ''}, ${updatedUserData.country || ''}`,
          };
        }
        return user;
      });
      
      setRows(updatedRows);
      setSaveLoading(false);
      handleCloseModal();

    } catch (error) {
      console.error('Error saving user:', error);
      setSaveLoading(false);
      // You could implement error handling UI here
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 60,
      renderCell: (params) => (
        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded-lg text-gray-700">
          {params.value}
        </span>
      )
    },
    { 
      field: 'fullName',
      headerName: 'Nome completo',
      width: 200,
      valueGetter: (params) => {
        // Add safety checks to prevent undefined errors
        if (!params || !params.row) return '';
        return `${params.row.firstName || ''} ${params.row.lastName || ''}`;
      },
      renderCell: (params) => {
        // Add safety checks to prevent undefined errors
        if (!params || !params.row) return null;
        
        return (
          <div className="flex items-center mt-2">
            <div className="w-8 h-8 bg-[#406580] rounded-full flex items-center justify-center text-white font-medium mr-2">
              {params.row.firstName ? params.row.firstName.charAt(0).toUpperCase() : ''}
              {params.row.lastName ? params.row.lastName.charAt(0).toUpperCase() : ''}
            </div>
            <div>
              <div className="font-medium">{params.value}</div>
              <div className="text-xs text-gray-500">{params.row.firstName} {params.row.lastName}</div>
            </div>
          </div>
        );
      }
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 230,
      renderCell: (params) => (
        <div className="flex items-center">
          <FiMail className="mr-2 text-gray-400" />
          <span>{params.value}</span>
        </div>
      ) 
    },
    { 
      field: 'role', 
      headerName: 'Ruolo', 
      width: 120,
      renderCell: (params) => {
        const roleColorMap = {
          'admin': 'bg-purple-100 text-purple-800 border-purple-200',
          'owner': 'bg-red-100 text-red-800 border-red-200',
          'user': 'bg-green-100 text-green-800 border-green-200'
        };
        
        const colorClass = roleColorMap[params.value] || 'bg-gray-100 text-gray-800 border-gray-200';
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass} border`}>
            {params.value}
          </span>
        );
      }
    },
    { 
      field: 'fullAddress', 
      headerName: 'Indirizzo', 
      width: 300,
      renderCell: (params) => (
        params.value && params.value.trim() !== ',' ? (
          <div className="flex items-center">
            <FiMapPin className="mr-2 text-gray-400" />
            <span className="truncate">{params.value}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">Indirizzo non specificato</span>
        )
      )
    },
    { 
      field: 'phone', 
      headerName: 'Telefono', 
      width: 150,
      renderCell: (params) => (
        <div className="flex items-center">
          <FiPhone className="mr-2 text-gray-400" />
          <span>{params.value}</span>
        </div>
      )
    },
    { 
      field: 'createdAt', 
      headerName: 'Registrazione', 
      width: 150,
      renderCell: (params) => (
        <div className="flex items-center">
          <FiCalendar className="mr-2 text-gray-400" />
          <span>{params.value}</span>
        </div>
      )
    },
    { 
      field: 'actions', 
      headerName: 'Azioni', 
      width: 120,
      renderCell: (params) => (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center justify-center px-4 py-1.5 mt-2 bg-[#406580] text-white rounded-md text-sm font-medium hover:bg-[#305570] transition-colors"
          onClick={() => handleEditClick(params.row.id)}
        >
          <FiEdit className="mr-1" size={14} />
          Modifica
        </motion.button>
      )
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-t-4 border-b-4 border-[#406580] rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Caricamento utenti...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.section 
      initial="hidden" 
      animate="visible" 
      variants={containerVariants} 
      className="w-full mt-1 max-w-[102rem]"
    >
      <motion.div variants={itemVariants} className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestione Clienti</h1>
          <p className="text-sm text-gray-600 mt-1 mb-2">Gestisci e visualizza tutti gli utenti della piattaforma</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <motion.div variants={itemVariants} className="bg-white rounded-lg border border-gray-200 flex items-center px-3 py-2 shadow-sm">
            <FiSearch className="text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Cerca utenti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-none focus:ring-0 text-sm outline-none"
            />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="role-filter-label">Ruolo</InputLabel>
              <Select
                labelId="role-filter-label"
                value={filterRole}
                label="Ruolo"
                onChange={(e) => setFilterRole(e.target.value)}
                sx={{ borderRadius: '0.5rem' }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
              </Select>
            </FormControl>
          </motion.div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Box sx={{ height: 750, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{ field: 'id', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            checkboxSelection
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f3f4f6',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f9fafb',
              },
            }}
          />
        </Box>
      </motion.div>

      <AnimatePresence>
        {selectedUser && (
          <Modal 
            open={Boolean(selectedUser)} 
            onClose={handleCloseModal}
            closeAfterTransition
            BackdropComponent={({ children }) => (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseModal}
                className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              >
                {children}
              </motion.div>
            )}
          >
            <div>
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute top-[125px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-[#406580] rounded-full flex items-center justify-center text-white font-medium mr-3">
                      {selectedUser.firstName ? selectedUser.firstName.charAt(0).toUpperCase() : ''}
                      {selectedUser.lastName ? selectedUser.lastName.charAt(0).toUpperCase() : ''}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">Modifica Utente</h2>
                      <p className="text-sm text-gray-500">{selectedUser.email}</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <TextField
                    label="Nome"
                    fullWidth
                    value={selectedUser.firstName || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, firstName: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiUser className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Cognome"
                    fullWidth
                    value={selectedUser.lastName || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, lastName: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiUser className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Email"
                    fullWidth
                    value={selectedUser.email}
                    onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiMail className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Telefono"
                    fullWidth
                    value={selectedUser.phone || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiPhone className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Indirizzo"
                    fullWidth
                    value={selectedUser.address || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, address: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiMapPin className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Città"
                    fullWidth
                    value={selectedUser.city || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, city: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiMapPin className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Codice Postale"
                    fullWidth
                    value={selectedUser.postalCode || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, postalCode: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiMapPin className="text-gray-400 mr-2" />,
                    }}
                  />
                  <TextField
                    label="Paese"
                    fullWidth
                    value={selectedUser.country || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, country: e.target.value })}
                    className="bg-gray-50"
                    InputProps={{
                      startAdornment: <FiMapPin className="text-gray-400 mr-2" />,
                    }}
                  />
                </div>

                <div className="mb-6">
                  <FormControl fullWidth>
                    <InputLabel id="role-select-label">Ruolo</InputLabel>
                    <Select
                      labelId="role-select-label"
                      value={selectedUser.role}
                      label="Ruolo"
                      onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                      startAdornment={<FiUserCheck className="text-gray-400 mr-2" />}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="owner">Owner</MenuItem>
                    </Select>
                  </FormControl>
                  <p className="text-xs text-gray-500 mt-1">
                    Cambiare il ruolo di un utente modifica i suoi permessi di accesso.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                  <Button 
                    variant="outlined" 
                    onClick={handleCloseModal}
                    sx={{ borderRadius: '0.5rem' }}
                  >
                    Annulla
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveUser}
                    disabled={saveLoading}
                    sx={{ 
                      backgroundColor: '#406580', 
                      '&:hover': { backgroundColor: '#305570' },
                      borderRadius: '0.5rem'
                    }}
                  >
                    {saveLoading ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Salvataggio...
                      </div>
                    ) : 'Salva Modifiche'}
                  </Button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                  <p>Utente registrato il: {new Date(selectedUser.rawCreatedAt).toLocaleString()}</p>
                  <p>ID: {selectedUser.id}</p>
                </div>
              </motion.div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.section>
  );
}